import { useState, useEffect, useCallback, useRef } from 'react'

// Custom hook to debounce state updates
export const useDebouncedState = (initialValue?: any, delay: number = 500) => {
  const [value, setValue] = useState(initialValue)
  const [debouncedValue, setDebouncedValue] = useState(initialValue)

  // Use a ref to store the timeout id, so it can be cleared both in the effect and the clear function
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Clear the previous timeout if any
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set timeout to update debouncedValue after the specified delay
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    // Cleanup the timeout if the component unmounts or if the value changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [value, delay])

  // Function to clear the debounced value immediately and invalidate any pending timeout
  const clearDebouncedValue = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current) // Clear any ongoing timeout
    }
    setDebouncedValue(initialValue)
    setValue(initialValue) // Optionally reset the immediate value too
  }, [initialValue])

  return [debouncedValue, setValue, clearDebouncedValue]
}